import React, { useRef, useState } from "react";
import { Heart, ChatCircle, ShareNetwork, MusicNotes, Gift, Lightning, DotsThree, BookmarkSimple, SpeakerHigh, Pause, Microphone, Repeat } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import api, { fileUrl } from "../lib/api";
import GiftModal from "./GiftModal";
import ReportModal from "./ReportModal";
import CaptionText from "./CaptionText";
import PostMedia from "./PostMedia";
import GiftBurst from "./GiftBurst";
import VoiceReactionsSheet from "./VoiceReactionsSheet";
import VerifiedBadge from "./VerifiedBadge";
import { useAuth } from "../context/AuthContext";

export default function PostCard({ post, onUpdate, onOpenComments, fallbackImage }) {
  const { user } = useAuth();
  const [liked, setLiked] = useState(post.liked);
  const [count, setCount] = useState(post.likes_count || 0);
  const [saved, setSaved] = useState(!!post.saved);
  const [reposted, setReposted] = useState(!!post.reposted);
  const [repostCount, setRepostCount] = useState(post.repost_count || 0);
  const [busy, setBusy] = useState(false);
  const [giftOpen, setGiftOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [burstGift, setBurstGift] = useState(null);
  const [vdPlaying, setVdPlaying] = useState(false);
  const [reactOpen, setReactOpen] = useState(false);
  const vdRef = useRef(null);

  const toggleVoiceDrop = () => {
    const a = vdRef.current;
    if (!a) return;
    if (vdPlaying) {
      a.pause();
      setVdPlaying(false);
    } else {
      a.currentTime = 0;
      a.play().catch(() => {});
      setVdPlaying(true);
    }
  };

  const toggleLike = async () => {
    if (busy) return;
    setBusy(true);
    setLiked((v) => !v);
    setCount((c) => c + (liked ? -1 : 1));
    try {
      const { data } = await api.post(`/posts/${post.id}/like`);
      setLiked(data.liked);
      onUpdate?.();
    } catch {
      setLiked(post.liked);
      setCount(post.likes_count);
    } finally {
      setBusy(false);
    }
  };

  const toggleSave = async () => {
    if (busy) return;
    setBusy(true);
    setSaved((v) => !v);
    try {
      const { data } = await api.post(`/posts/${post.id}/save`);
      setSaved(data.saved);
    } catch {
      setSaved(post.saved);
    } finally {
      setBusy(false);
    }
  };

  const isOwn = user && post.author && post.author.id === user.id;

  const toggleRepost = async () => {
    if (busy) return;
    setBusy(true);
    const next = !reposted;
    setReposted(next);
    setRepostCount((c) => c + (next ? 1 : -1));
    try {
      const { data } = await api.post(`/posts/${post.id}/repost`);
      setReposted(data.reposted);
    } catch {
      setReposted(!next);
      setRepostCount(post.repost_count || 0);
    } finally { setBusy(false); }
  };

  const boost = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/posts/${post.id}/boost`);
      onUpdate?.();
      alert("Post boosted for 24 hours! 🚀");
    } catch (e) {
      alert(e.response?.data?.detail || "Boost failed. Buy more Coins in your wallet.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid={`post-${post.id}`} className="relative h-[100dvh] w-full snap-start snap-always overflow-hidden bg-ink">
      <PostMedia post={post} fallbackImage={fallbackImage} />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent pointer-events-none" />

      {post.is_boosted && (
        <div className="absolute top-20 left-5 z-20 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-papaya/90 text-ink text-[10px] font-black tracking-[0.2em] uppercase animate-pulse-glow">
          <Lightning size={12} weight="fill" /> Boosted
        </div>
      )}
      {post.ai_labels?.length > 0 && (
        <div className={`absolute ${post.is_boosted ? "top-28" : "top-20"} left-5 z-20 flex items-center gap-1 px-2.5 py-1 rounded-full crystal-glass border border-white/15 text-cream/80 text-[9px] font-black tracking-[0.2em] uppercase`} data-testid={`post-${post.id}-ai-label`} title="This post uses AI-generated audio or visuals">
          ✦ {post.ai_labels.includes("ai_video") ? "AI generated" : post.ai_labels.includes("ai_song") ? "AI song" : "AI voice"}
        </div>
      )}

      {/* Top-right 3-dot menu */}
      <div className="absolute top-5 right-5 z-30">
        <button
          data-testid={`post-${post.id}-menu`}
          onClick={() => setMenuOpen((v) => !v)}
          className="w-9 h-9 rounded-full crystal-glass flex items-center justify-center text-cream"
        >
          <DotsThree size={20} weight="bold" />
        </button>
        {menuOpen && (
          <div className="absolute right-0 top-11 crystal-glass rounded-2xl border border-white/10 overflow-hidden min-w-[160px]">
            {!isOwn && (
              <button
                data-testid={`post-${post.id}-report`}
                onClick={() => { setReportOpen(true); setMenuOpen(false); }}
                className="w-full text-left px-4 py-3 text-sm text-red-300 hover:bg-red-500/10"
              >
                Report post
              </button>
            )}
            <button
              onClick={() => { navigator.clipboard?.writeText(window.location.origin + `/p/${post.id}`); setMenuOpen(false); }}
              className="w-full text-left px-4 py-3 text-sm text-cream hover:bg-white/5"
            >
              Copy link
            </button>
          </div>
        )}
      </div>

      {/* Right action stack */}
      <div className="absolute right-3 bottom-32 sm:bottom-36 flex flex-col items-center gap-5 z-20">
        <Link to={post.author ? `/u/${post.author.username}` : "#"} className="relative" data-testid={`post-${post.id}-author-avatar`}>
          <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-cream bg-ink">
            {post.author?.avatar_path ? (
              <img src={fileUrl(post.author.avatar_path)} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-papaya to-sea flex items-center justify-center text-ink font-black text-lg">
                {(post.author?.display_name || "?")[0]?.toUpperCase()}
              </div>
            )}
          </div>
          <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-papaya flex items-center justify-center text-ink font-black text-xs">+</span>
        </Link>

        <button
          data-testid={`post-${post.id}-like`}
          onClick={toggleLike}
          className="flex flex-col items-center gap-1 text-white hover:scale-110 transition-transform duration-300"
        >
          <div className={`w-12 h-12 rounded-full flex items-center justify-center crystal-glass ${liked ? "text-papaya" : ""}`}>
            <Heart size={28} weight={liked ? "fill" : "regular"} />
          </div>
          <span className="text-xs font-bold">{count}</span>
        </button>

        <button
          data-testid={`post-${post.id}-comments`}
          onClick={() => onOpenComments?.(post)}
          className="flex flex-col items-center gap-1 text-white hover:scale-110 transition-transform duration-300"
        >
          <div className="w-12 h-12 rounded-full flex items-center justify-center crystal-glass">
            <ChatCircle size={28} weight="duotone" />
          </div>
          <span className="text-xs font-bold">{post.comments_count || 0}</span>
        </button>

        <button
          data-testid={`post-${post.id}-repost`}
          onClick={toggleRepost}
          className="flex flex-col items-center gap-1 text-white hover:scale-110 transition-transform duration-300"
        >
          <div className={`w-12 h-12 rounded-full flex items-center justify-center crystal-glass ${reposted ? "text-papaya" : ""}`}>
            <Repeat size={28} weight={reposted ? "fill" : "regular"} />
          </div>
          <span className="text-xs font-bold">{repostCount > 0 ? repostCount : "Repost"}</span>
        </button>

        <button
          data-testid={`post-${post.id}-share`}
          className="flex flex-col items-center gap-1 text-white hover:scale-110 transition-transform duration-300"
          onClick={() => navigator.clipboard?.writeText(window.location.origin + `/p/${post.id}`)}
        >
          <div className="w-12 h-12 rounded-full flex items-center justify-center crystal-glass">
            <ShareNetwork size={28} weight="duotone" />
          </div>
          <span className="text-xs font-bold">Share</span>
        </button>

        <button
          data-testid={`post-${post.id}-save`}
          onClick={toggleSave}
          className="flex flex-col items-center gap-1 text-white hover:scale-110 transition-transform duration-300"
        >
          <div className={`w-12 h-12 rounded-full flex items-center justify-center crystal-glass ${saved ? "text-papaya" : ""}`}>
            <BookmarkSimple size={28} weight={saved ? "fill" : "regular"} />
          </div>
          <span className="text-xs font-bold">{saved ? "Saved" : "Save"}</span>
        </button>

        {isOwn ? (
          <button
            data-testid={`post-${post.id}-boost`}
            onClick={boost}
            className="flex flex-col items-center gap-1 text-white hover:scale-110 transition-transform duration-300"
          >
            <div className="w-12 h-12 rounded-full flex items-center justify-center crystal-glass text-papaya">
              <Lightning size={28} weight="fill" />
            </div>
            <span className="text-xs font-bold">Boost</span>
          </button>
        ) : (
          <button
            data-testid={`post-${post.id}-gift`}
            onClick={() => setGiftOpen(true)}
            className="flex flex-col items-center gap-1 text-white hover:scale-110 transition-transform duration-300"
          >
            <div className="w-12 h-12 rounded-full flex items-center justify-center bg-papaya text-ink papaya-glow">
              <Gift size={28} weight="fill" />
            </div>
            <span className="text-xs font-bold">Gift</span>
          </button>
        )}
      </div>

      {/* Bottom-left text overlay */}
      <div className="absolute left-4 right-24 bottom-28 sm:bottom-32 z-10">
        {post.voice_drop_path && (
          <>
            <audio
              ref={vdRef}
              src={fileUrl(post.voice_drop_path)}
              preload="none"
              onEnded={() => setVdPlaying(false)}
            />
            <div className="mb-2 flex items-center gap-2">
              <button
                data-testid={`post-${post.id}-voicedrop`}
                onClick={toggleVoiceDrop}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.15em] uppercase transition-colors ${
                  vdPlaying ? "bg-papaya text-ink animate-pulse" : "crystal-glass text-papaya"
                }`}
              >
                {vdPlaying ? <Pause size={12} weight="fill" /> : <SpeakerHigh size={12} weight="fill" />}
                Voice Drop{post.voice_drop_duration ? ` · ${Math.round(post.voice_drop_duration)}s` : ""}
              </button>
              <button
                data-testid={`post-${post.id}-voicereact`}
                onClick={() => setReactOpen(true)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full crystal-glass text-papaya text-[10px] font-black tracking-[0.15em] uppercase"
              >
                <Microphone size={12} weight="fill" />
                React{post.voice_reactions_count ? ` · ${post.voice_reactions_count}` : ""}
              </button>
            </div>
            <VoiceReactionsSheet post={post} open={reactOpen} onClose={() => setReactOpen(false)} />
          </>
        )}
        {post.author && (
          <Link to={`/u/${post.author.username}`} className="inline-flex items-center gap-1 text-cream font-black text-lg tracking-tight hover:text-papaya transition-colors">
            @{post.author.username}
            {post.author.is_verified && <VerifiedBadge size="sm" />}
          </Link>
        )}
        <p className="text-cream/90 text-sm mt-1 leading-relaxed line-clamp-3">{post.caption}</p>
        <div className="flex items-center gap-2 mt-3 text-cream/70 text-xs">
          <MusicNotes size={14} weight="fill" className="text-papaya" />
          {post.music ? (
            <span className="truncate max-w-[200px]">
              <span className="font-bold text-cream">{post.music.title}</span>
              <span className="text-cream/50"> · {post.music.artist}</span>
            </span>
          ) : (
            <span className="tracking-widest uppercase">Giffwurldaunce Original</span>
          )}
          <span className="flex items-center gap-0.5 ml-1" aria-hidden>
            <span className="w-1.5 h-1.5 rounded-full bg-jam-black ring-1 ring-white/20" />
            <span className="w-1.5 h-1.5 rounded-full bg-jam-green" />
            <span className="w-1.5 h-1.5 rounded-full bg-jam-gold" />
          </span>
        </div>
        {post.location && (
          <div className="flex items-center gap-1 mt-1.5 text-cream/50 text-[11px]">
            <span aria-hidden>📍</span>
            <span>{post.location}</span>
          </div>
        )}
      </div>

      {giftOpen && post.author && (
        <GiftModal
          recipient={post.author}
          post={post}
          onClose={() => setGiftOpen(false)}
          onSent={(g) => { setGiftOpen(false); setBurstGift(g); onUpdate?.(); }}
        />
      )}
      {burstGift && <GiftBurst gift={burstGift} onDone={() => setBurstGift(null)} />}
      {reportOpen && (
        <ReportModal
          target={{ type: "post", id: post.id }}
          onClose={() => setReportOpen(false)}
        />
      )}
    </div>
  );
}
